/*
 * Per-page history and blame, derived by scanning operation diffs.
 *
 * Neither exists as an endpoint: the API offers a diff per operation and nothing
 * per page or per line. Both questions are still answerable from what it does
 * serve — "which operations touched this page" is a filter over the diffs, and
 * "which operation wrote this paragraph" is the newest diff that ADDED a line
 * containing it — so they are answered here, in the client, rather than by asking
 * the app's own server to join anything. Recorded in API-GAPS.md.
 *
 * The cost is honest and visible: one request per operation, bounded, with the
 * progress reported so the reader knows the answer is "over the N operations
 * scanned" and not "over all history". Blame in particular is an approximation and
 * the UI says so — it finds where a line was introduced, not who owns it now.
 */
import { useCallback, useRef, useState } from 'react'
import {
  getOperationDiff,
  type DiffFile,
  type History,
  type HistoryOperation,
  type OperationDiff,
} from '../../../api/knowledgeBases.ts'

/** Operations scanned in one pass, so a long history cannot fire 400 requests. */
export const SCAN_BATCH = 25

export interface ScanProgress {
  done: number
  total: number
}

export interface PageTouch {
  op: HistoryOperation
  file: DiffFile
}

export interface BlameHit {
  op: HistoryOperation
  path: string
  line: string
}

export interface DiffScan {
  diffs: Map<string, OperationDiff>
  progress: ScanProgress | null
  scanning: boolean
  /** Ops whose diff could not be read, so a gap in the answer is visible. */
  failures: string[]
  scan: () => Promise<void>
  touchesOf: (path: string) => PageTouch[]
  blame: (fragment: string) => BlameHit | null
}

export function useDiffScan(kbId: string, history: History | null): DiffScan {
  const [diffs, setDiffs] = useState<Map<string, OperationDiff>>(new Map())
  const [progress, setProgress] = useState<ScanProgress | null>(null)
  const [scanning, setScanning] = useState(false)
  const [failures, setFailures] = useState<string[]>([])
  const opsRef = useRef<HistoryOperation[]>([])
  opsRef.current = history?.ops ?? []

  const scan = useCallback(async () => {
    const ops = opsRef.current.slice(0, SCAN_BATCH)
    if (ops.length === 0) return
    setScanning(true)
    setFailures([])
    setProgress({ done: 0, total: ops.length })
    const collected = new Map<string, OperationDiff>()
    const failed: string[] = []
    for (let index = 0; index < ops.length; index += 1) {
      const op = ops[index]
      if (!op) continue
      try {
        collected.set(op.op_id, await getOperationDiff(kbId, op.op_id))
      } catch {
        failed.push(op.op_id)
      }
      setProgress({ done: index + 1, total: ops.length })
    }
    setDiffs(collected)
    setFailures(failed)
    setScanning(false)
  }, [kbId])

  const touchesOf = useCallback(
    (path: string): PageTouch[] => {
      const touches: PageTouch[] = []
      for (const op of opsRef.current) {
        const diff = diffs.get(op.op_id)
        if (!diff) continue
        const file = (diff.files ?? []).find((candidate) => candidate.path === path)
        if (file) touches.push({ op, file })
      }
      return touches
    },
    [diffs],
  )

  const blame = useCallback(
    (fragment: string): BlameHit | null => {
      const needle = fragment.trim().toLowerCase()
      if (needle.length < 3) return null
      // `ops` is newest first, so the first match is the most recent introduction.
      for (const op of opsRef.current) {
        const diff = diffs.get(op.op_id)
        if (!diff) continue
        for (const file of diff.files ?? []) {
          for (const hunk of file.hunks ?? []) {
            for (const line of hunk.lines ?? []) {
              if (line.kind !== 'add') continue
              if (line.text.toLowerCase().includes(needle)) {
                return { op, path: file.path, line: line.text }
              }
            }
          }
        }
      }
      return null
    },
    [diffs],
  )

  return { diffs, progress, scanning, failures, scan, touchesOf, blame }
}

/** Every page path any scanned diff touched, for a page picker. */
export function scannedPaths(diffs: Map<string, OperationDiff>): string[] {
  const paths = new Set<string>()
  for (const diff of diffs.values()) {
    for (const file of diff.files ?? []) paths.add(file.path)
  }
  return [...paths].sort()
}
